import { useCallback, useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { isNumericId } from "@racoon/lib";
import { assignableLabels } from "~/shared/lib/organization-labels";
import {
  useListCustomerTypesQuery,
  useSetCounterpartyCustomerTypeMutation,
} from "~/shared/api/organization-labels/organization-labels.grpc";
import { useListCounterpartiesQuery } from "~/shared/api/organization/organization.grpc";

/**
 * The provider's stored customer type for a customer, "0" for none, undefined until it
 * is loaded. The type belongs to the provider–customer pair, not to Organization, so it
 * is read off the provider's counterparty list. Ids arrive as strings at runtime
 * (connectBaseQuery normalizes bigints), so they are compared as strings.
 */
export const useStoredCustomerTypeId = (
  providerId: number | null | undefined,
  customerId: number | undefined,
): string | undefined =>
  useListCounterpartiesQuery(
    { organizationId: BigInt(providerId ?? 0) },
    {
      skip: !providerId || customerId === undefined,
      selectFromResult: ({ currentData }) => ({
        storedTypeId: currentData
          ? String(
              currentData.counterparties.find(
                (c) => String(c.organization?.id) === String(customerId),
              )?.customerTypeId ?? 0,
            )
          : undefined,
      }),
    },
  ).storedTypeId;

/**
 * The provider's assignable customer types (see assignableLabels). Empty
 * when the provider has none: callers then show no type field at all.
 */
export const useCustomerTypeOptions = (
  providerId: number | null | undefined,
  currentId: string,
): ConnectProto.OrganizationLabel[] => {
  const { currentData } = useListCustomerTypesQuery(
    { ownerOrganizationIds: providerId ? [BigInt(providerId)] : [] },
    { skip: !providerId },
  );
  return useMemo(() => assignableLabels(currentData?.labels, currentId), [currentData, currentId]);
};

/**
 * Saves the provider's customer type for a customer. The type belongs to the
 * provider–customer pair, so a customer gets it only once it exists.
 */
export const useSetCustomerType = () => {
  const [setCustomerType] = useSetCounterpartyCustomerTypeMutation();
  return useCallback(
    (providerId: number, customerId: bigint, typeId: string) =>
      setCustomerType({
        organizationId: BigInt(providerId),
        counterpartyId: customerId,
        customerTypeId: isNumericId(typeId) ? BigInt(typeId) : 0n,
      }).unwrap(),
    [setCustomerType],
  );
};
